import { Component } from '@theme/component';

/**
 * Bikefanatics size guide: "What is my size?" calculator over product metafield custom.size_guide.
 * Algorithm per docs/sisend-suurusejuhis-integratsioonilt-2026-10-16.md (simplified Ridley / Cycling Factory rules).
 */
const SIZE_ORDER = ['XXXS', 'XXS', 'XS', 'S', 'M', 'L', 'XL', 'XXL', 'XXXL'];

class BfSizeGuide extends Component {
  requiredRefs = ['dialog', 'form', 'result', 'data', 'strings'];

  #urls = new Map();
  #resolving = null;

  get data() {
    try { return JSON.parse(this.refs.data.textContent || '{}'); } catch { return {}; }
  }
  get strings() {
    try { return JSON.parse(this.refs.strings.textContent || '{}'); } catch { return {}; }
  }

  open = () => {
    const { dialog } = this.refs;
    if (!dialog.open) dialog.showModal();
    dialog.addEventListener('click', this.#backdropClick);
    this.#resolveUrls();
    this.refs.heightInput?.focus();
  };

  close = () => {
    const { dialog } = this.refs;
    dialog.removeEventListener('click', this.#backdropClick);
    if (dialog.open) dialog.close();
  };

  #backdropClick = (event) => {
    if (event.target === this.refs.dialog) this.close();
  };

  calculate = async (event) => {
    event.preventDefault();
    await this.#resolveUrls();
    const data = this.data;
    const strings = this.strings;
    const sizes = Array.isArray(data.sizes) ? data.sizes : [];
    const height = Number.parseInt(this.refs.heightInput?.value ?? '', 10);
    const inseamRaw = this.refs.inseamInput?.value ?? '';
    const inseam = inseamRaw === '' ? null : Number.parseInt(inseamRaw, 10);
    const result = this.refs.result;

    this.querySelectorAll('.bf-size-guide__row--recommended').forEach((row) => row.classList.remove('bf-size-guide__row--recommended'));

    if (!Number.isFinite(height) || height < 140 || height > 215 || (inseam !== null && (inseam < 50 || inseam > 118))) {
      this.#show(result, `<span>${strings.invalid ?? ''}</span>`, true);
      return;
    }

    const inRange = (range, value) => Array.isArray(range) && range.length === 2 && value >= range[0] && value <= range[1];
    const order = (row) => { const i = SIZE_ORDER.indexOf(String(row.size).toUpperCase()); return i === -1 ? 100 + sizes.indexOf(row) : i; };
    const sortBySize = (list) => [...list].sort((a, b) => order(a) - order(b));

    const hFit = sortBySize(sizes.filter((row) => inRange(row.height, height)));
    let pick = null;

    if (data.mode === 'calc' && inseam !== null) {
      const iFit = sizes.filter((row) => inRange(row.inseam, inseam));
      const both = hFit.filter((row) => iFit.includes(row));
      if (both.length) {
        pick = both[0];
      } else if (hFit.length) {
        pick = hFit.reduce((best, row) => {
          const dist = (r) => {
            if (!Array.isArray(r.inseam)) return Number.POSITIVE_INFINITY;
            if (inseam < r.inseam[0]) return r.inseam[0] - inseam;
            if (inseam > r.inseam[1]) return inseam - r.inseam[1];
            return 0;
          };
          return dist(row) < dist(best) ? row : best;
        }, hFit[0]);
      }
    } else if (hFit.length) {
      pick = hFit[0];
    }

    const bikefitBtn = strings.bikefit_enabled
      ? `<a class="button" href="${strings.bikefit_url ?? '/pages/bikefit'}">${strings.bikefit_label ?? 'Bikefit'}</a>`
      : `<a class="button button-secondary" href="${strings.inquiry_url ?? '#'}">${strings.ask ?? ''}</a>`;
    if (!pick) {
      this.#show(result, `<span>${strings.out_of_range ?? ''}</span>${bikefitBtn}`, true);
      return;
    }
    // Borderline: height fits two or more sizes (overlapping ranges) -> suggest Bikefit for certainty.
    const borderline = hFit.length > 1 && strings.bikefit_enabled;
    const hint = borderline ? `<span class="bf-size-guide__hint">${strings.borderline ?? ''} <a href="${strings.bikefit_url ?? '/pages/bikefit'}">${strings.bikefit_label ?? 'Bikefit'}</a></span>` : '';

    const row = this.querySelector(`.bf-size-guide__row[data-size="${CSS.escape(String(pick.size))}"]`);
    row?.classList.add('bf-size-guide__row--recommended');

    const fits = Array.isArray(pick.height) ? (strings.result_fits ?? '').replace('%f', pick.height[0]).replace('%t', pick.height[1]) : '';
    const title = `<span><strong>${(strings.result ?? '%s').replace('%s', pick.size)}</strong> ${fits}</span>`;

    if (!pick.sku || (pick.sku !== this.dataset.currentSku && !this.#urls.has(pick.sku))) {
      this.#show(result, `<span>${(strings.not_in_shop ?? '%s').replace('%s', pick.size)}</span><a class="button button-secondary" href="${strings.inquiry_url ?? '#'}">${strings.ask ?? ''}</a>`, true);
      return;
    }
    if (pick.sku === this.dataset.currentSku) {
      this.#show(result, `${title}<span class="bf-size-guide__current">${strings.current ?? ''}</span>${hint}`, false);
      return;
    }
    const href = this.#urls.get(pick.sku);
    this.#show(result, `${title}<a class="button" href="${href}">${(strings.choose ?? '%s').replace('%s', pick.size)}</a>${hint}`, false);
  };

  #show(el, html, warn) {
    el.innerHTML = html;
    el.hidden = false;
    el.classList.toggle('bf-size-guide__result--warn', warn);
  }

  /** Resolve sibling product URLs by SKU through predictive search (variants.sku). */
  #resolveUrls() {
    if (this.#resolving) return this.#resolving;
    const base = this.dataset.searchUrl || '/search/suggest';
    const links = [...this.querySelectorAll('a[data-sku-link]')];
    const skus = [...new Set(links.map((a) => a.closest('tr')?.dataset.sku).filter(Boolean))];
    this.#resolving = Promise.all(
      skus.map(async (sku) => {
        try {
          const params = new URLSearchParams({ q: sku, 'resources[type]': 'product', 'resources[limit]': '3', 'resources[options][fields]': 'variants.sku' });
          const res = await fetch(`${base}.json?${params}`, { headers: { Accept: 'application/json' } });
          if (!res.ok) return;
          const json = await res.json();
          const products = json?.resources?.results?.products ?? [];
          const match = products.find((p) => Array.isArray(p.variants) ? p.variants.some((v) => v.sku === sku) : true) ?? products[0];
          if (match?.url) this.#urls.set(sku, match.url);
        } catch { /* keep fallback search link */ }
      })
    ).then(() => {
      const strings = this.strings;
      links.forEach((a) => {
        const sku = a.closest('tr')?.dataset.sku;
        const url = sku && this.#urls.get(sku);
        if (url) {
          a.setAttribute('href', url);
        } else {
          // SKU not in the shop (not published / not synced): offer the inquiry form instead of an empty search.
          a.setAttribute('href', strings.inquiry_url ?? '#');
          a.classList.add('bf-size-guide__link--ask');
          a.textContent = strings.ask ?? a.textContent;
          a.addEventListener('click', () => this.close());
        }
      });
    });
    return this.#resolving;
  }
}

if (!customElements.get('bf-size-guide')) customElements.define('bf-size-guide', BfSizeGuide);
